/**
 * AuthRedirectHandler Component
 *
 * Handles post-authentication navigation decisions.
 *
 * Logic:
 * 1. Fetch user's organisations from API
 * 2. Single org + single role → auto-redirect
 * 3. Admin only → redirect to admin app
 * 4. Multiple orgs/roles → navigate to /select
 * 5. No access → show error
 *
 * Token passing:
 * - The ID token is passed via URL fragment (#token=...) to maintain
 *   session continuity across different app origins.
 *
 * Updated (2026-02-25): Now uses useUserOrganisations hook for
 * organisation-aware navigation decisions.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { useUserOrganisations } from '../hooks';
import { Loading } from '../components';

/**
 * Retrieves and clears the returnTo URL from sessionStorage.
 * Returns null if not set or invalid.
 */
function getAndClearReturnTo(): string | null {
  const returnTo = sessionStorage.getItem('auth_returnTo');
  if (returnTo) {
    sessionStorage.removeItem('auth_returnTo');
    // Validate it's a valid URL
    try {
      new URL(returnTo);
      return returnTo;
    } catch {
      console.warn('[AuthRedirectHandler] Invalid returnTo URL:', returnTo);
      return null;
    }
  }
  return null;
}

/**
 * Redirect status for tracking the redirection process
 */
type RedirectStatus = 'loading' | 'redirecting' | 'error';

/**
 * AuthRedirectHandler Component
 *
 * Automatically redirects authenticated users based on their organisations.
 */
export function AuthRedirectHandler() {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading: authLoading,
    user,
    error: authError,
    logout,
  } = useAuth();
  const {
    isLoading: orgsLoading,
    decision,
    error: orgsError,
  } = useUserOrganisations();
  const [status, setStatus] = useState<RedirectStatus>('loading');

  useEffect(() => {
    // Wait for both auth and orgs to load
    if (authLoading || orgsLoading) {
      setStatus('loading');
      return;
    }

    // Not authenticated
    if (!isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    // Auth error
    if (authError) {
      setStatus('error');
      return;
    }

    // Orgs error
    if (orgsError) {
      setStatus('error');
      return;
    }

    // No decision yet
    if (!decision) {
      return;
    }

    // Check for returnTo URL first (cross-app login flow)
    const returnTo = getAndClearReturnTo();
    if (returnTo) {
      console.info('[AuthRedirectHandler] Using returnTo URL:', returnTo);
      setStatus('redirecting');
      const urlWithToken = user?.idToken
        ? `${returnTo}#token=${encodeURIComponent(user.idToken)}`
        : returnTo;
      window.location.replace(urlWithToken);
      return;
    }

    // Process navigation decision
    switch (decision.type) {
      case 'auto-redirect': {
        setStatus('redirecting');
        console.info(
          `[AuthRedirectHandler] Auto-redirect to ${decision.url}`,
          { orgSlug: decision.orgSlug, role: decision.role }
        );
        const targetUrl = user?.idToken
          ? `${decision.url}#token=${encodeURIComponent(user.idToken)}`
          : decision.url;
        window.location.replace(targetUrl);
        break;
      }

      case 'admin-only': {
        setStatus('redirecting');
        console.info('[AuthRedirectHandler] Admin-only redirect to admin app');
        const adminUrl = user?.idToken
          ? `${decision.url}#token=${encodeURIComponent(user.idToken)}`
          : decision.url;
        window.location.replace(adminUrl);
        break;
      }

      case 'show-selection': {
        console.info('[AuthRedirectHandler] Navigating to selection page', {
          orgCount: decision.organisations.length,
          isAdmin: decision.isAdmin,
        });
        navigate('/select', { replace: true });
        break;
      }

      case 'no-access': {
        console.warn('[AuthRedirectHandler] No access:', decision.reason);
        setStatus('error');
        break;
      }
    }
  }, [
    authLoading,
    orgsLoading,
    isAuthenticated,
    authError,
    orgsError,
    decision,
    user,
    navigate,
  ]);

  // Render based on status
  if (status === 'loading') {
    return <Loading fullScreen message="Loading your profile..." />;
  }

  if (status === 'redirecting') {
    return <Loading fullScreen message="Redirecting..." />;
  }

  if (status === 'error') {
    const errorMessage =
      authError?.message ||
      orgsError?.message ||
      (decision?.type === 'no-access' ? decision.reason : 'An error occurred');

    return (
      <div className="redirect-error">
        <h2>Unable to Continue</h2>
        <p>{errorMessage}</p>
        <button onClick={logout} className="error-back-link">
          Logout
        </button>
      </div>
    );
  }

  return <Loading fullScreen message="Processing..." />;
}

/**
 * ProtectedRoute Component
 *
 * A wrapper component that ensures the user is authenticated
 * before rendering children. If not authenticated, redirects to login.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Required roles to access this route (optional) */
  requiredRoles?: string[];
  /** Fallback component to show while loading */
  fallback?: React.ReactNode;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  fallback,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, roles } = useAuth();

  // Show loading state
  if (isLoading) {
    return <>{fallback ?? <Loading fullScreen message="Loading..." />}</>;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Use window.location for full page navigation
    window.location.replace('/');
    return <Loading fullScreen message="Redirecting to login..." />;
  }

  // Check required roles if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((required) =>
      roles.some((role) => role.toLowerCase() === required.toLowerCase())
    );

    if (!hasRequiredRole) {
      return (
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
          <p>Required roles: {requiredRoles.join(', ')}</p>
        </div>
      );
    }
  }

  return <>{children}</>;
}

